import { resolveUiLocale as resolveAppUiLocale } from '../../../shared/utils/localization';
import type { TokenUsageGranularity, TokenUsageSeriesPoint } from '../types/tokenUsage';

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

export const resolveUiLocale = (language?: string): string => resolveAppUiLocale(language);

/**
 * Calendar date (year/month/day) in the given IANA zone. Invalid or missing zones fall back to the
 * runtime local date so presets never depend on a hardcoded company timezone.
 */
export const calendarDateInTimeZone = (timeZone?: string | null, now: Date = new Date()): Date => {
  const zone = timeZone?.trim();
  if (zone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }).formatToParts(now);
      const year = Number(parts.find((part) => part.type === 'year')?.value);
      const month = Number(parts.find((part) => part.type === 'month')?.value);
      const day = Number(parts.find((part) => part.type === 'day')?.value);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    } catch {
      // Use the local calendar date when the zone id is not a valid IANA name.
    }
  }

  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const isValidTimeZone = (timeZone?: string | null): boolean => {
  const zone = timeZone?.trim();
  if (!zone) {
    return false;
  }

  try {
    Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Report boundaries are company-local wall clock time with no timezone suffix. They are anchored to
 * UTC here so formatting never shifts a bucket into the neighbouring day for a viewer in another
 * timezone; every formatter below therefore also renders in UTC.
 */
export const parseLocalDate = (value: string): Date | null => {
  const match = LOCAL_DATE_PATTERN.exec(value || '');
  if (!match) {
    return null;
  }

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
};

export const toDateInputValue = (value: string): string => {
  const match = LOCAL_DATE_PATTERN.exec(value || '');
  return match ? match[0] : '';
};

export const formatLocalDate = (
  value: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  const date = parseLocalDate(value);
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...options }).format(date);
};

export const formatNumber = (value: number, locale: string): string => (
  new Intl.NumberFormat(locale).format(value)
);

/** Axis labels stay readable at any scale, so large token counts collapse to 12k / 3.4M. */
export const formatCompactNumber = (value: number, locale: string): string => {
  if (value < 1000) {
    return new Intl.NumberFormat(locale).format(value);
  }

  if (value < 1000000) {
    const scaled = value / 1000;
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: scaled < 10 ? 1 : 0 }).format(scaled)}k`;
  }

  const scaled = value / 1000000;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: scaled < 10 ? 1 : 0 }).format(scaled)}M`;
};

export const formatPercent = (value: number, total: number, locale: string): string => {
  const ratio = total > 0 ? value / total : 0;

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: ratio > 0 && ratio < 0.1 ? 1 : 0,
  }).format(ratio);
};

/** Compact label for chart axes: the bucket start is enough to identify the period. */
export const formatBucketAxisLabel = (
  point: TokenUsageSeriesPoint,
  granularity: TokenUsageGranularity,
  locale: string,
): string => {
  if (granularity === 'month') {
    return formatLocalDate(point.fromLocal, locale, { month: 'short', year: '2-digit' });
  }

  return formatLocalDate(point.fromLocal, locale, { day: 'numeric', month: 'short' });
};

/** Full label for tooltips and the table, where a week needs its whole range. */
export const formatBucketRangeLabel = (
  point: TokenUsageSeriesPoint,
  granularity: TokenUsageGranularity,
  locale: string,
): string => {
  if (granularity === 'month') {
    return formatLocalDate(point.fromLocal, locale, { month: 'long', year: 'numeric' });
  }

  if (granularity === 'day') {
    return formatLocalDate(point.fromLocal, locale, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  const from = formatLocalDate(point.fromLocal, locale, { day: 'numeric', month: 'short' });
  const to = formatLocalDate(point.toLocal, locale, { day: 'numeric', month: 'short', year: 'numeric' });

  return from && to ? `${from} – ${to}` : from || to;
};

export const formatWindowRangeLabel = (
  fromLocal: string,
  toLocal: string,
  locale: string,
): string => {
  const from = formatLocalDate(fromLocal, locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const to = formatLocalDate(toLocal, locale, { day: 'numeric', month: 'short', year: 'numeric' });

  if (!from || !to) {
    return from || to;
  }

  return from === to ? from : `${from} – ${to}`;
};

/** Rounds a chart maximum up to a readable step so gridlines land on round numbers. */
export const resolveAxisMax = (maxValue: number): number => {
  if (maxValue <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(maxValue));
  const normalized = maxValue / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return step * magnitude;
};

const escapeCsvValue = (value: string | number): string => {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildCsv = (headers: string[], rows: Array<Array<string | number>>): string => (
  [headers, ...rows].map((row) => row.map(escapeCsvValue).join(',')).join('\n')
);

export const downloadCsv = (filename: string, csv: string): void => {
  // The BOM keeps Excel from mangling non-ASCII labels on open.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
